/**
 * 出庫單詳情 · 區塊（P1-7；`ui-spec` 頁 9）
 *
 * 為何抽離（硬規則 B：頁面檔 ≤200 行）：詳情頁一度 256 行 → 把「各行」同
 * 「執貨摘要＋下一步」兩塊搬入本檔，頁面只負責載入／標頭／組裝。
 *
 * 紅線
 *   · 「下一步」一律用後端回嘅 `next`（＝ `nextStatuses()`）—— 前端**唔准**自己列狀態
 *   · 「執貨摘要」嘅數字**一律唯讀**（後端 `pickSummary()` 推算）—— 冇人可以喺呢度改
 *   · ⭐ **步驟按角色分開**（`decisions/018`）：發通知／取消／場後結算／完單 ＝ **職員／管理員**；
 *     倉（物流）只做「執貨」同「交收一掣」—— 動作區住 `OutboundNextSteps.tsx`（本檔只放摘要）
 *   · `direct_sale` 交收完仲未完：要場後結算（賣出／剩貨／損壞）才准 `closed`
 */
import { useTranslation } from 'react-i18next';
import { NextStepsSection } from './OutboundNextSteps';
import { statusLabelKey } from './OutboundStatusChip';
import { card, hint as hintStyle } from '@coeldery/ui-kit';
import type { Me, OutboundDetail as Detail, OutboundLine } from '../lib/api';

/** 單頭一行「標籤：值」（值空就由呼叫者唔顯示） */
export function Field({ labelKey, value }: { labelKey: string; value: string }) {
  const { t } = useTranslation();
  return (
    <p style={{ margin: '2px 0', fontSize: '14px' }}>
      <span style={{ color: 'var(--color-text-secondary)' }}>{t(labelKey)}：</span>
      {value}
    </p>
  );
}

/** 各行：申報 vs 實際（執貨後）；顯示倉內可用箱數俾人對照 */
export function LinesSection({ lines }: { lines: OutboundLine[] }) {
  const { t } = useTranslation();
  return (
    <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 800 }}>{t('out.lines')}</h3>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {lines.map((l) => (
          <li key={l.id} style={{ padding: '8px 0', borderTop: '1px solid var(--color-divider)' }}>
            <strong>{l.itemName ?? '—'}</strong>
            <span style={hintStyle}> {l.barcode ?? ''}</span>
            {l.batchNo && <span style={hintStyle}> · {t('out.batch')}：{l.batchNo}</span>}
            <p style={hintStyle}>
              {t('out.cartons')}：{l.cartons} · {t('out.cbm')}：{l.cbm}
              {l.pickedCartons !== null ? ` · ${t('out.picked')}：${l.pickedCartons}` : ''}
              {` · ${t('out.warehouseAvailable', { n: l.warehouseCartons })}`}
            </p>
            {l.pickedNote && (
              <p style={{ ...hintStyle, color: 'var(--color-accent)' }}>
                {t('out.pickNote')}：{l.pickedNote}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 「執貨摘要」（**唯讀**）＋ 下一步（按角色；`decisions/018`） */
export function SummarySection({
  detail,
  user,
  onChanged,
}: {
  detail: Detail;
  user: Me;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const order = detail.order;
  if (!order) return null;

  const s = detail.summary;

  return (
    <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 800 }}>{t('out.summary')}</h3>
      <p style={hintStyle}>
        {t('out.itemCount')}：{s.itemCount} · {t('out.declaredCartons')}：{s.declaredCartons}
        {s.pendingLines === 0 ? ` · ${t('out.picked')}：${s.pickedCartons}` : ''}
        {s.pendingLines > 0 ? ` · ${t('out.pendingLines')}：${s.pendingLines}` : ''}
        {/* ⚠️ 未執完**唔准**報「執少箱數」—— 未執唔等於執少（2026-09-28 用戶實測投訴） */}
        {s.pendingLines === 0 && s.shortfallCartons !== 0 ? ` · ${t('out.shortfall')}：${s.shortfallCartons}` : ''}
      </p>
      {order.pickDiscrepancy && (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }}>
          {t('out.pickDiscrepancy')}：{order.pickDiscrepancy}
        </p>
      )}
      {detail.next.length > 0 && (
        <p style={hintStyle}>
          {t('out.next')}：{detail.next.map((n) => t(statusLabelKey(n))).join('／')}
        </p>
      )}

      {/* ⭐ 下一步（按角色；`decisions/018`）：倉只係「執貨＋交收一掣」；發通知／取消／
          場後結算／完單只 85 職員／管理員見到 —— 動作一律住 `OutboundNextSteps.tsx` */}
      <NextStepsSection detail={detail} user={user} onChanged={onChanged} />
    </section>
  );
}
